import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import formatTranslation from '@/utils/formatTranslation';

const PERSON_COLOR_PALETTE = [
  { background: 'rgba(59, 130, 246, 0.18)', border: 'rgba(37, 99, 235, 0.7)', text: '#1D4ED8' }, // blue
  { background: 'rgba(34, 197, 94, 0.18)', border: 'rgba(22, 163, 74, 0.7)', text: '#15803D' }, // green
  { background: 'rgba(244, 114, 182, 0.18)', border: 'rgba(236, 72, 153, 0.65)', text: '#BE185D' }, // pink
  { background: 'rgba(250, 204, 21, 0.24)', border: 'rgba(217, 119, 6, 0.65)', text: '#B45309' }, // amber
  { background: 'rgba(248, 113, 113, 0.18)', border: 'rgba(220, 38, 38, 0.65)', text: '#B91C1C' }, // red
  { background: 'rgba(129, 140, 248, 0.18)', border: 'rgba(99, 102, 241, 0.65)', text: '#4C1D95' }, // indigo
  { background: 'rgba(56, 189, 248, 0.18)', border: 'rgba(14, 165, 233, 0.65)', text: '#0369A1' }, // sky
  { background: 'rgba(45, 212, 191, 0.18)', border: 'rgba(20, 184, 166, 0.65)', text: '#0F766E' }, // teal
];

const TODAY_SHADOW_STYLE = {
  boxShadow: '0 0 24px rgba(37, 99, 235, 0.18)',
};

const getPersonKey = (request) =>
  request?.employee?.id ??
  request?.employee?.email ??
  request?.employee?.name ??
  request?.id;

const CalendarEntry = ({
  request,
  palette,
  showRole = false,
  dayPartLabel = null,
  showDayPartSuffix = false,
  translate,
}) => {
  const translateLabel = (key, fallback) =>
    typeof translate === 'function' ? translate(key, fallback) : fallback;
  const personName = request?.employee?.name ?? translateLabel('defaults.positionFallback', 'Team Member');
  const personRole =
    request?.employee?.position ?? request?.employee?.role ?? '';
  const formattedType =
    translateLabel(`types.${request?.type}`, (request?.type || 'Absence')?.replace(/-/g, ' '));
  const labelSegments = showRole && personRole
    ? [personName, personRole, formattedType]
    : [personName, formattedType];
  const baseLabel = labelSegments.join(' - ');
  const displayLabel =
    showDayPartSuffix && dayPartLabel
      ? `${baseLabel} (${dayPartLabel})`
      : baseLabel;

  return (
    <div
      className="text-[11px] px-1.5 py-1 rounded border font-medium leading-tight"
      style={{
        backgroundColor: palette.background,
        borderColor: palette.border,
        color: palette.text,
      }}
      title={displayLabel}
    >
      {showRole ? (
        <>
          <div className="leading-tight">{personName}</div>
          {personRole && (
            <div className="text-[10px] leading-tight opacity-80">
              {personRole}
            </div>
          )}
          <div className="text-[10px] leading-tight opacity-80">
            {formattedType}
          </div>
          {showDayPartSuffix && dayPartLabel && (
            <div className="text-[10px] leading-tight opacity-80">
              {dayPartLabel}
            </div>
          )}
        </>
      ) : (
        displayLabel
      )}
    </div>
  );
};

const DaySegment = ({
  label,
  requests,
  segmentKey,
  personColorMap,
  showRole = false,
  showHeader = true,
  translate,
}) => {
  const countLabel = requests.length > 0 ? String(requests.length) : '';
  const contentWrapperClasses = ['space-y-1'];

  if (!showHeader && countLabel) {
    contentWrapperClasses.push('mt-1');
  }

  return (
    <div
      className={`rounded-sm border border-border/60 p-1 min-h-[42px] flex flex-col gap-1 flex-1 ${
        requests.length ? 'bg-muted/40' : 'bg-muted/10'
      }`}
    >
      {showHeader ? (
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>{label}</span>
          <span className="text-muted-foreground/60">{countLabel}</span>
        </div>
      ) : (
        countLabel && (
          <div className="flex items-center justify-end text-[10px] uppercase tracking-wide text-muted-foreground">
            <span className="text-muted-foreground/60">{countLabel}</span>
          </div>
        )
      )}
      {requests.length > 0 ? (
        <div className={contentWrapperClasses.join(' ')}>
          {requests.slice(0, 2).map((request, index) => (
            <CalendarEntry
              key={`${segmentKey}-${request?.id ?? index}`}
              request={request}
              palette={
                personColorMap.get(getPersonKey(request)) ??
                PERSON_COLOR_PALETTE[0]
              }
              showRole={showRole}
              translate={translate}
            />
          ))}
          {requests.length > 2 && (
            <div className="text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground">
              {formatTranslation(
                translate?.('calendar.more', '+{{count}} more') ?? '+{{count}} more',
                { count: requests.length - 2 },
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1" />
      )}
    </div>
  );
};

const normalizeDate = (value) => {
  const normalized = new Date(value);
  normalized?.setHours(0, 0, 0, 0);
  return normalized;
};

const isSameDay = (a, b) =>
  a?.getFullYear() === b?.getFullYear() &&
  a?.getMonth() === b?.getMonth() &&
  a?.getDate() === b?.getDate();

const getWeekInfo = (date) => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const startOfYear = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const daysSinceStart = Math.floor((target - startOfYear) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.floor((daysSinceStart + startOfYear.getUTCDay()) / 7) + 1;

  return { weekNumber, year: date.getFullYear() };
};

const getStartOfWeek = (date) => {
  const start = new Date(date);
  const day = start.getDay();
  start.setDate(start.getDate() - day);
  start.setHours(0, 0, 0, 0);
  return start;
};

const CalendarView = ({ timeOffData, onDateSelect, locale = 'en-US', translate }) => {
  const localize = (key, fallback) =>
    typeof translate === 'function' ? translate(key, fallback) : fallback;
  const initialIsMobile =
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 1023px)').matches
      : false;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(initialIsMobile ? 'week' : 'month');
  const [isMobile, setIsMobile] = useState(initialIsMobile);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');

    const updateIsMobile = () => {
      setIsMobile(mediaQuery.matches);
    };

    updateIsMobile();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateIsMobile);
    } else {
      mediaQuery.addListener(updateIsMobile);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', updateIsMobile);
      } else {
        mediaQuery.removeListener(updateIsMobile);
      }
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setViewMode('week');
    }
  }, [isMobile]);

  const monthNames = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) =>
        new Date(2020, index, 1).toLocaleDateString(locale, { month: 'long' }),
      ),
    [locale],
  );
  const dayNames = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) => {
        const reference = new Date(2020, 5, index + 1);
        return reference.toLocaleDateString(locale, { weekday: 'short' });
      }),
    [locale],
  );

  const personColorMap = useMemo(() => {
    if (!Array.isArray(timeOffData)) {
      return new Map();
    }

    const mapping = new Map();
    let colorIndex = 0;

    timeOffData?.forEach((request) => {
      const person = getPersonKey(request);

      if (!person || mapping.has(person)) {
        return;
      }

      const paletteEntry = PERSON_COLOR_PALETTE[colorIndex % PERSON_COLOR_PALETTE.length];
      mapping.set(person, paletteEntry);

      colorIndex += 1;
    });

    return mapping;
  }, [timeOffData]);

  const getDaysInMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0)?.getDate();

  const getFirstDayOfMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth(), 1)?.getDay();

  const handleNavigate = (direction) => {
    setCurrentDate((prevDate) => {
      const nextDate = new Date(prevDate);

      if (viewMode === 'month') {
        nextDate.setMonth(prevDate.getMonth() + direction);
      } else {
        nextDate.setDate(prevDate.getDate() + direction * 7);
      }

      return nextDate;
    });
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleViewModeChange = (mode) => {
    if (isMobile && mode === 'month') {
      return;
    }

    setViewMode(mode);
  };

  const getDateRequests = (date) => {
    const checkDate = normalizeDate(date);

    return timeOffData?.filter((request) => {
      const startDate = normalizeDate(request.startDate);
      const endDate = normalizeDate(request.endDate);

      return (
        checkDate >= startDate &&
        checkDate <= endDate &&
        request?.status === 'approved'
      );
    });
  };

  const partitionRequestsBySegment = (date) => {
    const segments = { forenoon: [], afternoon: [], fullDay: [] };
    const requestsForDate = getDateRequests(date) ?? [];

    requestsForDate.forEach((request) => {
      const startDate = request?.startDate ? normalizeDate(request.startDate) : null;
      const endDate = request?.endDate ? normalizeDate(request.endDate) : null;

      if (!startDate || !endDate) {
        segments.forenoon.push(request);
        segments.afternoon.push(request);
        return;
      }

      const isSingleDayRequest = isSameDay(startDate, endDate);
      const dayPart = isSingleDayRequest && typeof request?.dayPart === 'string'
        ? request.dayPart
        : 'full-day';

      const safeDayPart =
        dayPart === 'forenoon' || dayPart === 'afternoon' || dayPart === 'full-day'
          ? dayPart
          : 'full-day';

      if (safeDayPart === 'full-day') {
        segments.fullDay.push(request);
      }

      if (safeDayPart === 'full-day' || safeDayPart === 'forenoon') {
        segments.forenoon.push(request);
      }

      if (safeDayPart === 'full-day' || safeDayPart === 'afternoon') {
        segments.afternoon.push(request);
      }
    });

    return segments;
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = normalizeDate(new Date());

    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className="h-24 border border-border bg-muted/30"
        ></div>
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      const segments = partitionRequestsBySegment(dayDate);
      const fullDayRequests = segments.fullDay ?? [];
      const fullDaySet = new Set(fullDayRequests);
      const forenoonOnly = (segments.forenoon ?? []).filter(
        (request) => !fullDaySet.has(request)
      );
      const afternoonOnly = (segments.afternoon ?? []).filter(
        (request) => !fullDaySet.has(request)
      );
      const monthEntries = [
        ...fullDayRequests.map((request) => ({
          request,
          dayPartLabel: null,
        })),
        ...forenoonOnly.map((request) => ({
          request,
          dayPartLabel: 'Fornoon',
        })),
        ...afternoonOnly.map((request) => ({
          request,
          dayPartLabel: 'Afternoon',
        })),
      ];
      const isToday = isSameDay(dayDate, today);

      days.push(
        <div
          key={day}
          className={`h-32 p-1 cursor-pointer hover:bg-muted/50 transition-smooth flex flex-col ${
            isToday
              ? 'bg-primary/10 border-2 border-primary ring-4 ring-primary/40'
              : 'border border-border bg-card'
          }`}
          onClick={() => {
            const selectedDate = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              day
            );
            setCurrentDate(selectedDate);
            onDateSelect?.(selectedDate);
          }}
          style={isToday ? TODAY_SHADOW_STYLE : undefined}
        >
          <div
            className={`text-sm font-medium mb-1 ${
              isToday ? 'text-primary' : 'text-foreground'
            }`}
          >
            {day}
          </div>
          <div className="flex flex-col gap-1 flex-1">
            {monthEntries.length > 0 ? (
              <div className="space-y-1">
                {monthEntries.slice(0, 2).map(({ request, dayPartLabel }, index) => {
                  const palette =
                    personColorMap.get(getPersonKey(request)) ??
                    PERSON_COLOR_PALETTE[0];

                  return (
                    <CalendarEntry
                      key={`${day}-month-${request?.id ?? index}`}
                      request={request}
                      palette={palette}
                      showDayPartSuffix={Boolean(dayPartLabel)}
                      dayPartLabel={dayPartLabel}
                    />
                  );
                })}
                {monthEntries.length > 2 && (
                  <div className="text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground">
                    +{monthEntries.length - 2} more
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 rounded-sm border border-dashed border-border/60 bg-muted/10" />
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const renderWeekHeaders = () => {
    const startOfWeek = getStartOfWeek(currentDate);
    const today = normalizeDate(new Date());

    return dayNames.map((day, index) => {
      const headerDate = new Date(startOfWeek);
      headerDate.setDate(startOfWeek.getDate() + index);
      const monthLabel = monthNames[headerDate.getMonth()]?.slice(0, 3);
      const isToday = isSameDay(headerDate, today);

      return (
        <div
          key={`${day}-${index}`}
          className={`p-2 text-center text-sm font-medium border-b border-border ${
            isToday ? 'text-primary bg-primary/10' : 'text-muted-foreground'
          }`}
        >
          <div>{day}</div>
          <div
            className={`text-xs ${
              isToday ? 'text-primary/80' : 'text-muted-foreground'
            }`}
          >
            {monthLabel} {headerDate.getDate()}
          </div>
        </div>
      );
    });
  };

  const renderWeekRows = () => {
    const startOfWeek = getStartOfWeek(currentDate);
    const today = normalizeDate(new Date());

    const weekDays = Array.from({ length: 7 }).map((_, index) => {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + index);
      const segments = partitionRequestsBySegment(dayDate);
      const isToday = isSameDay(dayDate, today);

      return {
        dayDate,
        segments,
        isToday,
      };
    });

    return {
      forenoon: [...weekDays],
      afternoon: [...weekDays],
    };
  };

  const renderWeekGrid = () => {
    const weekRows = renderWeekRows();
    const hasForenoonSegments = weekRows.forenoon.some(({ segments }) => {
      const fullDaySet = new Set(segments?.fullDay ?? []);
      return (segments?.forenoon ?? []).some((request) => !fullDaySet.has(request));
    });
    const hasAfternoonSegments = weekRows.afternoon.some(({ segments }) => {
      const fullDaySet = new Set(segments?.fullDay ?? []);
      return (segments?.afternoon ?? []).some((request) => !fullDaySet.has(request));
    });

    const segmentsConfig = [];

    if (hasForenoonSegments) {
      segmentsConfig.push({ label: localize('calendar.labels.forenoon', 'Forenoon'), key: 'forenoon' });
    }

    if (hasAfternoonSegments) {
      segmentsConfig.push({ label: localize('calendar.labels.afternoon', 'Afternoon'), key: 'afternoon' });
    }

    const hasPartialRows = segmentsConfig.length > 0;
    const rowDefinitions = hasPartialRows
      ? segmentsConfig
      : [{ label: null, key: 'full-day' }];
    const totalRows = hasPartialRows ? segmentsConfig.length : 1;
    const handledFullDayColumns = new Set();
    const cells = [];

    rowDefinitions.forEach((segment, rowIndex) => {
      const gridRow = rowIndex + 1;
      const isLastRow = rowIndex === rowDefinitions.length - 1;

      if (segment.label) {
        cells.push(
          <div
            key={`label-${segment.key}`}
            style={{ gridRow, gridColumn: 1 }}
            className={`p-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-r border-border bg-muted/30 flex items-center justify-center ${
              isLastRow ? '' : 'border-b'
            }`}
          >
            {segment.label}
          </div>
        );
      }

      const rowData = weekRows[hasPartialRows ? segment.key : 'forenoon'] ?? [];

      rowData.forEach(({ dayDate, segments, isToday }, columnIndex) => {
        const column = hasPartialRows ? columnIndex + 2 : columnIndex + 1;
        const fullDayRequests = segments?.fullDay ?? [];
        const fullDaySet = new Set(fullDayRequests);
        const forenoonRequests = (segments?.forenoon ?? []).filter(
          (request) => !fullDaySet.has(request)
        );
        const afternoonRequests = (segments?.afternoon ?? []).filter(
          (request) => !fullDaySet.has(request)
        );

        const columnKeyBase = dayDate.toISOString();
        const baseClasses = [
          isMobile ? 'min-h-[480px]' : 'min-h-[160px]',
          'p-1.5',
          'cursor-pointer',
          'hover:bg-muted/50',
          'transition-smooth',
          'flex',
          'flex-col',
          'border',
        ];

        let spanRows = 1;
        let cellContent = null;

        if (fullDayRequests.length > 0 && !handledFullDayColumns.has(columnIndex)) {
          spanRows = hasPartialRows ? totalRows : 1;
          handledFullDayColumns.add(columnIndex);

          if (hasPartialRows && totalRows > 1) {
            baseClasses.push('border-b');
          }

          if (isToday) {
            baseClasses.push('bg-primary/10', 'border-primary', 'ring-4', 'ring-primary/40');
          } else {
            baseClasses.push('bg-card', 'border-border');
          }

          const hasPartialRequests =
            forenoonRequests.length > 0 || afternoonRequests.length > 0;

          cellContent = (
            <div className="flex flex-col gap-1 h-full">
              <DaySegment
                label={localize('calendar.labels.fullDay', 'Full Day')}
                segmentKey={`${columnKeyBase}-full`}
                requests={fullDayRequests}
                personColorMap={personColorMap}
                showRole
                translate={localize}
              />
              {hasPartialRows && hasPartialRequests && (
                <div
                  className={`grid gap-1 flex-1 ${
                    hasForenoonSegments && hasAfternoonSegments
                      ? 'grid-rows-2'
                      : 'grid-rows-1'
                  }`}
                >
                  {hasForenoonSegments && (
                    <DaySegment
                      label={localize('calendar.labels.forenoon', 'Forenoon')}
                      segmentKey={`${columnKeyBase}-full-am`}
                      requests={forenoonRequests}
                      personColorMap={personColorMap}
                      showRole
                      showHeader={false}
                    translate={localize}
                  />
                  )}
                  {hasAfternoonSegments && (
                    <DaySegment
                      label={localize('calendar.labels.afternoon', 'Afternoon')}
                      segmentKey={`${columnKeyBase}-full-pm`}
                      requests={afternoonRequests}
                      personColorMap={personColorMap}
                      showRole
                      showHeader={false}
                    translate={localize}
                  />
                  )}
                </div>
              )}
            </div>
          );
        } else if (handledFullDayColumns.has(columnIndex)) {
          return;
        } else {
          const requests =
            segment.key === 'forenoon'
              ? forenoonRequests
              : segment.key === 'afternoon'
                ? afternoonRequests
                : [];

          if (segment.key === 'forenoon' && hasAfternoonSegments) {
            baseClasses.push('border-b');
          }
          if (segment.key === 'afternoon' && hasForenoonSegments) {
            baseClasses.push('border-t-0');
          }

          if (isToday) {
            baseClasses.push('bg-primary/10', 'border-primary', 'ring-4', 'ring-primary/40');
          } else {
            baseClasses.push('bg-card', 'border-border');
          }

          const showHeaderForCell = !hasPartialRows && requests.length > 0;

          cellContent = (
            <DaySegment
              label={segment.label ?? localize('calendar.labels.fullDay', 'Full Day')}
              segmentKey={`${columnKeyBase}-${segment.key}`}
              requests={requests}
              personColorMap={personColorMap}
              showRole
              showHeader={showHeaderForCell}
            translate={localize}
          />
          );
        }

        const gridRowValue =
          spanRows > 1 ? `${gridRow} / span ${spanRows}` : String(gridRow);
        const applyShadow = isToday && rowIndex === 0;
        const cellStyle = {
          gridRow: gridRowValue,
          gridColumn: column,
          ...(applyShadow ? TODAY_SHADOW_STYLE : {}),
        };

        cells.push(
          <div
            key={`${segment.key}-${columnKeyBase}`}
            className={baseClasses.filter(Boolean).join(' ')}
            style={cellStyle}
            onClick={() => {
              const selectedDate = new Date(dayDate);
              setCurrentDate(selectedDate);
              onDateSelect?.(selectedDate);
            }}
          >
            {cellContent}
          </div>
        );
      });
    });

    return { cells, hasPartialRows };
  };

  const { weekNumber, year: weekYear } = getWeekInfo(currentDate);
  const monthLabel = `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  const weekLabel = formatTranslation(
    localize('calendar.weekLabel', 'Week {{week}} in {{year}}'),
    { week: weekNumber, year: weekYear },
  );
  const activeLabel = viewMode === 'month' ? monthLabel : weekLabel;
  const weekGridResult = viewMode === 'week' ? renderWeekGrid() : null;
  const weekGridCells = weekGridResult?.cells ?? [];
  const hasPartialRows = weekGridResult?.hasPartialRows ?? false;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleViewModeChange('month')}
            disabled={isMobile}
          >
            {localize('calendar.actions.month', 'Month View')}
          </Button>
          <Button
            variant={viewMode === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleViewModeChange('week')}
          >
            {localize('calendar.actions.week', 'Week View')}
          </Button>
        </div>
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleNavigate(-1)}
          >
            <Icon name="ChevronLeft" size={16} />
          </Button>
          <span className="text-lg font-semibold text-foreground whitespace-nowrap">
            {activeLabel}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleNavigate(1)}
          >
            <Icon name="ChevronRight" size={16} />
          </Button>
        </div>
        <div className="flex items-center justify-end">
          <Button variant="outline" size="sm" onClick={handleToday}>
            {localize('calendar.actions.today', 'Today')}
          </Button>
        </div>
      </div>
      {viewMode === 'month' ? (
        <>
          <div className="grid grid-cols-7 gap-0 mb-2">
              {dayNames.map((day) => (
              <div
                key={day}
                className="p-2 text-center text-sm font-medium text-muted-foreground border-b border-border"
              >
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0 border border-border rounded-lg overflow-hidden">
            {renderCalendarDays()}
          </div>
        </>
      ) : (
        <>
          {hasPartialRows ? (
            <div
              className="grid gap-0 mb-2"
              style={{ gridTemplateColumns: '80px repeat(7, minmax(0, 1fr))' }}
            >
              <div className="p-2 border-b border-r border-border bg-muted/30" />
              {renderWeekHeaders()}
            </div>
          ) : (
            <div
              className="grid gap-0 mb-2"
              style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
            >
              {renderWeekHeaders()}
            </div>
          )}
          <div
            className="grid gap-0 border border-border rounded-lg overflow-hidden"
            style={{
              gridTemplateColumns: hasPartialRows
                ? '80px repeat(7, minmax(0, 1fr))'
                : 'repeat(7, minmax(0, 1fr))',
            }}
          >
            {weekGridCells}
          </div>
        </>
      )}
      <div className="flex items-center justify-center space-x-6 mt-4 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded bg-primary/20 border border-primary"></div>
          <span className="text-muted-foreground">
            {localize('calendar.legend.today', 'Today')}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded bg-warning/20"></div>
          <span className="text-muted-foreground">
            {localize('calendar.legend.absence', 'Absence')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
